/**
 * Almikia Epic PL (TASK-061): pure derivation of the context captured with a
 * feedback report. No React, no I/O: unit-tested in `feedbackContext.test.ts`.
 *
 * Privacy: the title comes only from the route/resource ("Leads",
 * "Operación ALM-BEA-26-00001"); it never contains a contact or client name,
 * and the query string (which may carry search terms) is never captured.
 */

/** Column caps from the `feedback_reports` CHECK constraints (TASK-060). */
export const SCREEN_PATH_MAX = 300;
export const SCREEN_TITLE_MAX = 120;
export const VIEWPORT_MAX = 20;
export const APP_LOCALE_MAX = 10;
export const USER_AGENT_MAX = 300;

type Translate = (key: string, options?: Record<string, unknown>) => string;

export type ScreenContext = {
  screen_path: string;
  screen_title: string;
};

const RESOURCE_SEGMENT = /^[a-z][a-z0-9_]*$/;
const OPERATION_CODE = /^[A-Za-z0-9][A-Za-z0-9-]*$/;
const FIXED_PAGES: Record<string, string> = {
  settings: "crm.settings.title",
  profile: "crm.profile.title",
};

const cap = (value: string, max: number) => value.slice(0, max);

const safeDecode = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

/**
 * Translates a key and returns `null` when it has no translation, so an
 * unknown resource falls back to the generic title instead of a raw key.
 */
const tryTranslate = (
  translate: Translate,
  key: string,
  options: Record<string, unknown> = {},
): string | null => {
  const text = translate(key, { ...options, _: "" });
  return text && text !== key ? text : null;
};

const deriveTitle = (pathname: string, translate: Translate): string => {
  const unknownTitle = translate("crm.feedback.screen_unknown");
  const [resource, id] = pathname.split("/").filter(Boolean).map(safeDecode);

  if (!resource) return translate("crm.feedback.screen_dashboard");

  const fixedKey = FIXED_PAGES[resource];
  if (fixedKey) return tryTranslate(translate, fixedKey) ?? unknownTitle;

  if (!RESOURCE_SEGMENT.test(resource)) return unknownTitle;
  const nameKey = `resources.${resource}.name`;

  if (id === undefined) {
    return tryTranslate(translate, nameKey, { smart_count: 2 }) ?? unknownTitle;
  }

  const singular = tryTranslate(translate, nameKey, { smart_count: 1 });
  if (!singular) return unknownTitle;

  if (id === "create") {
    return translate("crm.feedback.screen_create", { name: singular });
  }
  // The operation's public code is its id; it is the only record identifier
  // shown, and only when it has the plain code shape.
  if (resource === "operations" && OPERATION_CODE.test(id)) {
    return translate("crm.feedback.screen_operation", { code: id });
  }
  // The trailing action (show/edit) is ignored: both are "the record".
  return translate("crm.feedback.screen_detail", { name: singular });
};

/**
 * Maps the router path to the report's `screen_path` / `screen_title`, both
 * capped to their database column lengths. Unknown routes fall back to a
 * generic Spanish title.
 */
export const deriveScreenContext = (
  pathname: string,
  translate: Translate,
): ScreenContext => ({
  screen_path: cap(pathname || "/", SCREEN_PATH_MAX),
  screen_title: cap(deriveTitle(pathname || "/", translate), SCREEN_TITLE_MAX),
});

export type ClientContext = {
  viewport: string;
  app_locale: string | null;
  user_agent: string | null;
};

/**
 * Viewport ("390x844"), app locale and a short user agent, capped to their
 * column lengths. Inputs are passed in so the function stays pure.
 */
export const deriveClientContext = (input: {
  innerWidth: number;
  innerHeight: number;
  locale?: string | null;
  userAgent?: string | null;
}): ClientContext => ({
  viewport: cap(
    `${Math.round(input.innerWidth)}x${Math.round(input.innerHeight)}`,
    VIEWPORT_MAX,
  ),
  app_locale: input.locale ? cap(input.locale, APP_LOCALE_MAX) : null,
  user_agent: input.userAgent ? cap(input.userAgent, USER_AGENT_MAX) : null,
});
